// src/services/api.js
import axios from 'axios';
import { API_BASE_URL } from '../config';

console.log('Configuring API with base URL:', API_BASE_URL);

const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Function to get CSRF token from cookie
const getCsrfTokenFromCookie = () => {
    return document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
};

// Function to fetch CSRF token
const fetchCsrfToken = async () => {
    try {
        const response = await api.get('/csrf/', {
            withCredentials: true
        });
        
        // Wait for cookie to be set
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const token = getCsrfTokenFromCookie();
        if (token) {
            api.defaults.headers.common['X-CSRFToken'] = token;
            return token;
        }
        
        if (response.data.csrfToken) {
            api.defaults.headers.common['X-CSRFToken'] = response.data.csrfToken;
            return response.data.csrfToken;
        }
        
        console.warn('No CSRF token found');
        return null;
    } catch (error) {
        console.error('Error fetching CSRF token:', error);
        return null;
    }
};

// Add request interceptor
api.interceptors.request.use(async config => {
    // Skip CSRF for certain endpoints
    if (config.url === '/csrf/') {
        return config;
    }

    // Ensure we have a CSRF token for non-GET requests
    if (config.method !== 'get') {
        let token = getCsrfTokenFromCookie();
        
        // If no token in cookie, fetch new one
        if (!token) {
            token = await fetchCsrfToken();
        }
        
        if (token) {
            config.headers['X-CSRFToken'] = token;
        }
    }

    return config;
});

// Add response interceptor
api.interceptors.response.use(
    response => response,
    async error => {
        // Handle 403 CSRF errors
        if (error.response?.status === 403 && 
            (error.response?.data?.detail?.includes('CSRF') || !getCsrfTokenFromCookie())) {
            try {
                // Clear existing token
                delete api.defaults.headers.common['X-CSRFToken'];
                
                // Fetch new token
                const token = await fetchCsrfToken();
                
                if (token && error.config) {
                    // Retry the failed request with new token
                    error.config.headers['X-CSRFToken'] = token;
                    return axios(error.config);
                }
            } catch (retryError) {
                console.error('Error retrying request with new CSRF token:', retryError);
            }
        }
        return Promise.reject(error);
    }
);

export default api;
export { fetchCsrfToken };
